import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Home, ArrowLeft, CheckCircle } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

export default function ForgotPasswordPage() {
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username) {
      toast.error("Please enter your username");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { username });
      setSubmitted(true);
      toast.success("Password reset request sent to admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo/Brand */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
            <Home className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-primary">WilyRash Homes</h1>
            <p className="text-sm text-text-muted">Tenant Management System</p>
          </div>
        </div>

        <Card className="border-stone-200 shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold">
              {submitted ? "Request Submitted" : "Forgot Password"}
            </CardTitle>
            <CardDescription>
              {submitted 
                ? "Your request has been sent to the administrator"
                : "Enter your username to request a password reset. An email with your username will be sent to the administrator."
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-text-muted mb-6">
                  The administrator has been notified of your password reset request with your username. 
                  They will reset your password and contact you with a temporary password. 
                  You will be required to change this password on your next login.
                </p>
                <Link to="/login">
                  <Button 
                    className="w-full h-12 rounded-full bg-primary hover:bg-primary-800 text-white font-medium"
                    data-testid="back-to-login-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    data-testid="forgot-password-username-input"
                    type="text"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="h-12 bg-stone-50 border-stone-200 focus:border-primary"
                  />
                </div>

                <Button 
                  type="submit" 
                  data-testid="forgot-password-submit-btn"
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary-800 text-white font-medium transition-all active:scale-95"
                  disabled={loading}
                >
                  {loading ? "Sending Request..." : "Request Password Reset"}
                </Button>

                <div className="text-center">
                  <Link 
                    to="/login" 
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    data-testid="back-to-login-link"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
